import type { SessionIntent } from '../types/prioritization/session';
import { normalizeSessionToolId } from './sessionToolCatalog';

/** Suitability scores (1–5) for each tool per session intent. 5 = ideal fit, 1 = poor fit. */
export const TOOL_SUITABILITY_BY_INTENT: Record<SessionIntent, Record<string, number>> = {
  rank_priorities: {
    pairwise_ranking: 5,
    stack_ranking: 5,
    max_priorities_pyramid: 5,
    ahp: 5,
    simple_weighted_scoring: 4,
    rice: 4,
    wsjf: 4,
    magic_prioritization: 4,
    dot_voting: 3,
    impact_effort_matrix: 3,
    moscow: 3,
    pareto_analysis: 3,
    buy_a_feature: 3,
    cost_of_delay: 3,
    eisenhower_matrix: 2,
    fast_vs_right: 2,
    prune_product_tree: 2,
    speed_boat: 1,
  },
  select_item_pool: {
    moscow: 5,
    prune_product_tree: 5,
    eisenhower_matrix: 4,
    impact_effort_matrix: 4,
    speed_boat: 4,
    fast_vs_right: 4,
    dot_voting: 4,
    pareto_analysis: 4,
    buy_a_feature: 3,
    stack_ranking: 3,
    pairwise_ranking: 3,
    magic_prioritization: 3,
    max_priorities_pyramid: 3,
    simple_weighted_scoring: 2,
    rice: 2,
    wsjf: 2,
    cost_of_delay: 2,
    ahp: 2,
  },
  select_attributes: {
    pairwise_ranking: 5,
    ahp: 5,
    stack_ranking: 4,
    dot_voting: 4,
    simple_weighted_scoring: 4,
    moscow: 3,
    fast_vs_right: 3,
    impact_effort_matrix: 2,
    eisenhower_matrix: 2,
    magic_prioritization: 2,
    max_priorities_pyramid: 2,
    speed_boat: 2,
    buy_a_feature: 2,
    prune_product_tree: 2,
    pareto_analysis: 2,
    cost_of_delay: 1,
    rice: 1,
    wsjf: 1,
  },
  converge_values: {
    simple_weighted_scoring: 5,
    rice: 5,
    wsjf: 5,
    cost_of_delay: 5,
    ahp: 4,
    dot_voting: 4,
    impact_effort_matrix: 4,
    magic_prioritization: 4,
    buy_a_feature: 4,
    pairwise_ranking: 3,
    moscow: 3,
    stack_ranking: 3,
    max_priorities_pyramid: 3,
    prune_product_tree: 3,
    eisenhower_matrix: 3,
    fast_vs_right: 2,
    pareto_analysis: 2,
    speed_boat: 2,
  },
  calibration: {
    ahp: 5,
    simple_weighted_scoring: 4,
    pairwise_ranking: 4,
    dot_voting: 3,
    rice: 3,
    wsjf: 3,
    stack_ranking: 3,
    impact_effort_matrix: 3,
    moscow: 3,
    magic_prioritization: 3,
    max_priorities_pyramid: 3,
    buy_a_feature: 3,
    fast_vs_right: 3,
    cost_of_delay: 3,
    pareto_analysis: 2,
    eisenhower_matrix: 2,
    prune_product_tree: 2,
    speed_boat: 2,
  },
  review: {
    speed_boat: 5,
    prune_product_tree: 4,
    moscow: 4,
    dot_voting: 4,
    pareto_analysis: 4,
    eisenhower_matrix: 4,
    impact_effort_matrix: 3,
    buy_a_feature: 3,
    stack_ranking: 3,
    pairwise_ranking: 3,
    magic_prioritization: 3,
    max_priorities_pyramid: 3,
    fast_vs_right: 3,
    simple_weighted_scoring: 2,
    rice: 2,
    wsjf: 2,
    cost_of_delay: 2,
    ahp: 2,
  },
};

const DEFAULT_SUITABILITY = 3;

/** Resolve 1–5 suitability for a tool given session intent (aliases normalized). */
export function getToolSuitabilityScore(
  intent: SessionIntent | undefined,
  toolId: string | undefined | null,
): number {
  const resolvedIntent = intent ?? 'rank_priorities';
  const canonical = normalizeSessionToolId(toolId) ?? toolId;
  if (!canonical) return DEFAULT_SUITABILITY;
  return TOOL_SUITABILITY_BY_INTENT[resolvedIntent]?.[canonical] ?? DEFAULT_SUITABILITY;
}
